/**
 * Utility class for handling dates consistently across the application.
 * 
 * Backend Contract:
 * - All datetime fields are returned in 'Y-m-d H:i:s' format (e.g., '2024-01-15 14:30:00')
 * - Date-only fields are also returned in 'Y-m-d H:i:s' format (with time as '00:00:00')
 * - This ensures consistent parsing without timezone ambiguity
 * 
 * Two Use Cases:
 * 1. Date-Only Fields: date_due, date_paid (payments) - display/edit date only
 * 2. DateTime Fields: arrival, departure, show_round, timeline datetime - split into date + time controls
 */
export class DateUtil {
  /**
   * Safely parses a date/datetime value from the backend and returns a Date object or null
   * @param value - The date value from backend (expected format: 'Y-m-d H:i:s' or Date object)
   * @returns Date object if valid, null otherwise
   */
  static parseDate(value: any): Date | null {
    if (!value) return null;
    
    // If it's already a Date object, return it
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    
    // If it's a string, we expect backend format: 'Y-m-d H:i:s' (e.g., '2024-01-15 14:30:00')
    if (typeof value === 'string') {
      try {
        // Backend sends: '2024-01-15 14:30:00'
        // JavaScript Date constructor handles this format correctly
        const date = new Date(value);
        return isNaN(date.getTime()) ? null : date;
      } catch (error) {
        console.warn('DateUtil: Failed to parse backend date:', value, error);
        return null;
      }
    }
    
    // Fallback for unexpected types
    console.warn('DateUtil: Unexpected date value type:', typeof value, value);
    return null;
  }

  /**
   * Safely parses a date from backend and returns it formatted as YYYY-MM-DD string for date inputs
   * Use for: date_due, date_paid, start_date, end_date form controls
   * @param value - The date value from backend
   * @returns Formatted date string (YYYY-MM-DD) or null
   */
  static parseDateToString(value: any): string | null {
    const date = this.parseDate(value);
    if (!date) return null;

    try {
      // Return in YYYY-MM-DD format for date inputs using local date (no UTC conversion)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.warn('DateUtil: Failed to format date to string:', value, error);
      return null;
    }
  }

  /**
   * Safely parses a datetime from backend and returns the time portion as HH:mm:ss string
   * Use for: arrival_time, departure_time, show_round_time form controls
   * @param value - The datetime value from backend (format: 'Y-m-d H:i:s')
   * @returns Formatted time string (HH:mm:ss) or null
   */
  static parseTimeToString(value: any): string | null {
    const date = this.parseDate(value);
    if (!date) return null;
    
    try {
      // Return in HH:mm:ss format for time inputs
      return date.toTimeString().split(' ')[0];
    } catch (error) {
      console.warn('DateUtil: Failed to format time to string:', value, error);
      return null;
    }
  }

  /**
   * Safely parses a date from backend and returns it formatted as ISO string for calendar components
   * @param value - The date value from backend
   * @returns ISO string or empty string for FullCalendar
   */
  static formatDateForCalendar(value: any): string {
    const date = this.parseDate(value);
    if (!date) return '';
    
    try {
      return date.toISOString();
    } catch (error) {
      console.warn('DateUtil: Failed to format date for calendar:', value, error);
      return '';
    }
  }

  /**
   * Splits a backend datetime string into separate date and time parts for split form controls
   * Use for: setting arrival/departure/show_round form values from backend data
   * @param value - The datetime value from backend (format: 'Y-m-d H:i:s')
   * @returns Object with date and time strings, or null values if parsing fails
   */
  static splitDateTime(value: any): { date: string | null; time: string | null } {
    const date = this.parseDate(value);
    if (!date) {
      return { date: null, time: null };
    }

    try {
      // Use local date methods to avoid timezone conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      return {
        date: dateString,  // YYYY-MM-DD
        time: date.toTimeString().split(' ')[0]  // HH:mm:ss
      };
    } catch (error) {
      console.warn('DateUtil: Failed to split datetime:', value, error);
      return { date: null, time: null };
    }
  }

  /**
   * Combines separate date and time strings into a backend-compatible datetime string
   * Use for: combining arrival+arrival_time form values before sending to backend
   * @param dateStr - Date string in YYYY-MM-DD format
   * @param timeStr - Time string in HH:mm:ss format
   * @returns Combined datetime string in 'Y-m-d H:i:s' format, or null if invalid
   */
  static combineDateTime(dateStr: string | null, timeStr: string | null): string | null {
    if (!dateStr || !timeStr) return null;
    
    try {
      // Combine and validate
      const combined = `${dateStr} ${timeStr}`;
      const date = new Date(combined);
      
      if (isNaN(date.getTime())) {
        console.warn('DateUtil: Invalid combined datetime:', combined);
        return null;
      }
      
      // Return in backend format: YYYY-MM-DD HH:mm:ss
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.warn('DateUtil: Failed to combine datetime:', dateStr, timeStr, error);
      return null;
    }
  }
}